import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { ArrowDown, ArrowLeft, ArrowUp, Plus, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import {
  ASSESSMENT_QUESTION_TYPE_LABELS,
  ASSESSMENT_SECTIONS_MAX,
  DEFAULT_TEST_PASS_PERCENT,
  SECTION_MINUTES_MAX,
  drawMarksProblem,
  isAutoMarked,
  testTotals,
  type AssessmentInput,
  type AssessmentView,
  type QuestionView,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { Checkbox } from '../../../components/Checkbox';
import { Chip } from '../../../components/Chip';
import { Input, Textarea } from '../../../components/Input';
import { Select } from '../../../components/Select';
import { SettingRow } from '../../../components/SettingRow';
import { SkeletonLines } from '../../../components/Skeleton';
import { ForbiddenState, NotFoundState } from '../../../components/system-states/SystemState';
import { ApiError, errorMessage } from '../../../lib/api-client';
import { useModule } from '../../../lib/modules';
import { wholeNumber } from '../../../lib/whole-number';
import { useCanEdit, useCanView } from '../../../stores/session';
import { useToasts } from '../../../stores/toasts';
import { useUnsavedWarning } from '../../../lib/use-unsaved-warning';
import { useVentures } from '../../organisation/api/organisation-api';
import { useSaveTest, useTest } from '../api/assessments-api';
import { AssessmentsHeader } from '../components/AssessmentsHeader';
import { QuestionEditor } from '../components/QuestionEditor';
import { QuestionPicker } from '../components/QuestionPicker';
import styles from '../components/Assessments.module.css';
import builder from './TestBuilderPage.module.css';

/**
 * ASM-02 — one test (F9.02): timed sections, each giving every candidate all its
 * questions or N of them at random (S17 Q1), shuffled or not, and a suggested pass mark
 * the job copies when it picks the test (S17 Q5). Saving an existing test writes its
 * next version; jobs keep the version they chose.
 */
export function TestBuilderPage(): ReactNode {
  const { testId } = useParams({ from: '/app-layout/assessments/tests/$testId' });
  const canView = useCanView('assessment_template');
  const test = useTest(testId === 'new' ? null : testId);

  if (!canView) return <ForbiddenState resource="tests" contact="Your admin" />;
  if (testId !== 'new' && test.isPending) return <SkeletonLines count={10} />;
  if (testId !== 'new' && (test.error || !test.data)) return <NotFoundState />;
  return <Builder key={test.data?.id ?? 'new'} existing={test.data ?? null} />;
}

interface PooledQuestion {
  readonly familyId: string;
  readonly questionText: string;
  readonly typeLabel: string;
  readonly points: number;
  readonly isAutoMarked: boolean;
  readonly hasNewerVersion: boolean;
}

interface SectionDraft {
  readonly rowId: string;
  readonly name: string;
  readonly instructions: string;
  readonly durationMinutes: number;
  readonly isDrawing: boolean;
  readonly drawCount: number;
  readonly questions: readonly PooledQuestion[];
}

const DEFAULT_SECTION_MINUTES = 10;

function plural(count: number, word: string): string {
  return `${count} ${word}${count === 1 ? '' : 's'}`;
}

function pooled(question: QuestionView, hasNewerVersion = false): PooledQuestion {
  return {
    familyId: question.familyId,
    questionText: question.questionText,
    typeLabel: ASSESSMENT_QUESTION_TYPE_LABELS[question.type],
    points: question.points,
    isAutoMarked: isAutoMarked(question.type, question.acceptedAnswers),
    hasNewerVersion,
  };
}

function newSection(index: number): SectionDraft {
  return { rowId: crypto.randomUUID(), name: `Section ${index + 1}`, instructions: '', durationMinutes: DEFAULT_SECTION_MINUTES, isDrawing: false, drawCount: 1, questions: [] };
}

function sectionsOf(test: AssessmentView | null): readonly SectionDraft[] {
  if (!test) return [newSection(0)];
  return test.sections.map((section) => ({
    rowId: section.id,
    name: section.name,
    instructions: section.instructions ?? '',
    durationMinutes: section.durationMinutes,
    isDrawing: section.drawCount !== null,
    drawCount: section.drawCount ?? section.questions.length,
    questions: section.questions.map((question) => pooled(question, question.hasNewerVersion)),
  }));
}

function inputOf(name: string, description: string, passPercent: number, isShuffled: boolean, isOffline: boolean, sections: readonly SectionDraft[]): AssessmentInput {
  return {
    name: name.trim(),
    description: description.trim() || null,
    passPercent,
    isShuffled,
    isOffline,
    sections: sections.map((section) => ({
      name: section.name.trim(),
      instructions: section.instructions.trim() || null,
      durationMinutes: section.durationMinutes,
      drawCount: section.isDrawing && section.drawCount < section.questions.length ? section.drawCount : null,
      questionFamilyIds: section.questions.map((question) => question.familyId),
    })),
  };
}

function Builder({ existing }: { readonly existing: AssessmentView | null }): ReactNode {
  const canEdit = useCanEdit('assessment_template');
  const save = useSaveTest();
  const ventures = useVentures();
  const navigate = useNavigate();
  const show = useToasts((state) => state.show);
  const [name, setName] = useState(existing?.name ?? '');
  const [description, setDescription] = useState(existing?.description ?? '');
  const [passPercent, setPassPercent] = useState(existing?.passPercent ?? DEFAULT_TEST_PASS_PERCENT);
  const [isShuffled, setIsShuffled] = useState(existing?.isShuffled ?? true);
  const [isOffline, setIsOffline] = useState(existing?.isOffline ?? false);
  const isExamSessionsOn = useModule('exam_sessions');
  const [ventureId, setVentureId] = useState(existing?.ventureId ?? '');
  const [sections, setSections] = useState<readonly SectionDraft[]>(() => sectionsOf(existing));
  const [picking, setPicking] = useState<number | null>(null);
  const [isWriting, setIsWriting] = useState(false);
  const [serverErrors, setServerErrors] = useState<Readonly<Record<string, string>>>({});
  const [isDirty, setIsDirty] = useState(false);
  useUnsavedWarning(isDirty);

  const ventureList = (ventures.data ?? []).map((venture) => ({ id: venture.id, name: venture.name }));
  const chosenVenture = ventureId || ventureList[0]?.id || '';
  const isReadOnly = !canEdit;
  const totals = testTotals(sections.map((section) => ({ durationMinutes: section.durationMinutes, drawCount: section.isDrawing ? section.drawCount : null, points: section.questions.map((question) => question.points) })));
  const given = sections.reduce((sum, section) => sum + (section.isDrawing ? Math.min(section.drawCount, section.questions.length) : section.questions.length), 0);
  const hasWritten = sections.some((section) => section.questions.some((question) => !question.isAutoMarked));
  const sectionProblems = sections.map((section) => {
    if (section.questions.length === 0) return 'Add at least one question.';
    if (section.durationMinutes < 1) return 'Give the section at least 1 minute.';
    if (section.isDrawing && (section.drawCount < 1 || section.drawCount > section.questions.length)) return `Give between 1 and ${section.questions.length}.`;
    return drawMarksProblem(section.isDrawing ? section.drawCount : null, section.questions.map((question) => question.points));
  });
  const isValid = name.trim() !== '' && chosenVenture !== '' && passPercent >= 1 && passPercent <= 100 && sectionProblems.every((problem) => problem === null);

  function touch(): void {
    setIsDirty(true);
  }
  function setSection(index: number, patch: Partial<SectionDraft>): void {
    setSections((current) => current.map((section, i) => (i === index ? { ...section, ...patch } : section)));
    touch();
  }
  function move(index: number, questionIndex: number, by: -1 | 1): void {
    const questions = [...(sections[index]?.questions ?? [])];
    const target = questionIndex + by;
    const moving = questions[questionIndex];
    const other = questions[target];
    if (!moving || !other) return;
    questions[questionIndex] = other;
    questions[target] = moving;
    setSection(index, { questions });
  }
  function addQuestions(index: number, picked: readonly QuestionView[]): void {
    const section = sections[index];
    if (!section) return;
    const have = new Set(section.questions.map((question) => question.familyId));
    setSection(index, { questions: [...section.questions, ...picked.filter((question) => !have.has(question.familyId)).map((question) => pooled(question))] });
    setPicking(null);
  }

  async function onSave(): Promise<void> {
    setServerErrors({});
    try {
      const saved = await save.mutateAsync({ id: existing?.id ?? null, version: existing?.version ?? 0, ventureId: chosenVenture, assessment: inputOf(name, description, passPercent, isShuffled, isOffline, sections) });
      setIsDirty(false);
      show({
        tone: 'success',
        message: existing ? `${saved.name} saved as version ${saved.versionNumber}` : `${saved.name} saved`,
        detail: existing && existing.jobsUsing > 0 ? `Jobs using version ${existing.versionNumber} keep it until someone picks the new one in the job's setup.` : 'A job can choose it in its setup, step 5.',
      });
      void navigate({ to: '/assessments' });
    } catch (cause) {
      setServerErrors(cause instanceof ApiError ? cause.fields : {});
      show({ tone: 'danger', message: 'The test was not saved', detail: errorMessage(cause) });
    }
  }

  return (
    <div className={builder.screen}>
      <AssessmentsHeader current="tests" />
      <Link to="/assessments" className={styles.back}>
        <ArrowLeft size={14} aria-hidden />
        Tests
      </Link>

      <div className={builder.columns}>
        <div className={builder.main}>
          <Card isFlush>
            <div>
              <div className={builder.head}>
                <h2 className={builder.headTitle}>{existing ? existing.name : 'New test'}</h2>
                {existing && (
                  <span className={builder.headMeta}>
                    Version {existing.versionNumber}. Saving makes version {existing.versionNumber + 1}
                  </span>
                )}
              </div>
              <SettingRow label="Name" isRequired error={serverErrors['assessment.name']}>
                {({ inputId, describedBy, isInvalid }) => <span className={builder.fill}><Input id={inputId} aria-describedby={describedBy} isInvalid={isInvalid} autoFocus={!existing} disabled={isReadOnly} value={name} placeholder="Excel basics" onChange={(event) => { setName(event.target.value); touch(); }} /></span>}
              </SettingRow>
              {!existing && ventureList.length > 1 && (
                <SettingRow label="Venture" isRequired>
                  {({ inputId }) => <span className={builder.fill}><Select id={inputId} label="Venture" value={chosenVenture} options={ventureList.map((venture) => ({ value: venture.id, label: venture.name }))} onChange={(value) => { setVentureId(value); touch(); }} /></span>}
                </SettingRow>
              )}
              <SettingRow label="Suggested pass mark" hint="Copied into a job when it picks this test. The job can change it.">
                {({ inputId, describedBy }) => (
                  <span className={builder.unit}>
                    <Input id={inputId} aria-describedby={describedBy} isNumeric inputMode="numeric" suffix="%" disabled={isReadOnly} value={String(passPercent)} onChange={(event) => { setPassPercent(Math.min(100, wholeNumber(event.target.value))); touch(); }} />
                  </span>
                )}
              </SettingRow>
              <SettingRow label="What it tests" isOptional isWide hint="Candidates see this before they start.">
                {({ inputId, describedBy }) => <span className={builder.fill}><Textarea id={inputId} aria-describedby={describedBy} rows={2} disabled={isReadOnly} value={description} onChange={(event) => { setDescription(event.target.value); touch(); }} /></span>}
              </SettingRow>
              <div className={builder.plainRow}>
                <Checkbox checked={isShuffled} disabled={isReadOnly} label="Shuffle the questions and their choices for each candidate" onChange={(checked) => { setIsShuffled(checked); touch(); }} />
              </div>
              {/* Exam sessions is a module (§L34): while it is off, a test is always a link — unless it was already set to paper. */}
              {(isExamSessionsOn || isOffline) && (
                <div className={builder.plainRow}>
                  <Checkbox checked={isOffline} disabled={isReadOnly} label="Sat on paper at an exam session, not sent as a link" onChange={(checked) => { setIsOffline(checked); touch(); }} />
                  {isOffline && <p className={builder.note}>A job using it books people on exam sessions. Each person gets one total mark, typed in after the exam, and the minutes set how long each session runs.</p>}
                </div>
              )}
            </div>
          </Card>

          {sections.map((section, index) => {
            const problem = sectionProblems[index] ?? serverErrors[`sections.${index}.drawCount`] ?? serverErrors[`sections.${index}.questionFamilyIds`];
            const title = section.name || `Section ${index + 1}`;
            return (
              <section key={section.rowId} aria-label={title}>
                <Card isFlush>
                  <div>
                    <div className={builder.head}>
                      <span className={`${builder.badge} ${builder.sectionBadge}`} aria-hidden>
                        {index + 1}
                      </span>
                      <h2 className={builder.headTitle}>{title}</h2>
                      <span className={builder.headMeta}>
                        {plural(section.questions.length, 'question')} in the pool · {section.durationMinutes} minutes
                      </span>
                      {!isReadOnly && sections.length > 1 && (
                        <span className={builder.headAction}>
                          <Button size="sm" variant="ghost" icon={Trash2} onClick={() => { setSections(sections.filter((_, i) => i !== index)); touch(); }}>
                            Remove section
                          </Button>
                        </span>
                      )}
                    </div>
                    <SettingRow label="Section name" isRequired>
                      {({ inputId }) => <span className={builder.fill}><Input id={inputId} disabled={isReadOnly} value={section.name} onChange={(event) => setSection(index, { name: event.target.value })} /></span>}
                    </SettingRow>
                    <SettingRow label="Time for this section" hint={`Up to ${SECTION_MINUTES_MAX} minutes. It starts when the previous section ends.`}>
                      {({ inputId, describedBy }) => (
                        <span className={builder.unitWide}>
                          <Input id={inputId} aria-describedby={describedBy} isNumeric inputMode="numeric" suffix="minutes" disabled={isReadOnly} value={String(section.durationMinutes)} onChange={(event) => setSection(index, { durationMinutes: Math.min(SECTION_MINUTES_MAX, wholeNumber(event.target.value)) })} />
                        </span>
                      )}
                    </SettingRow>
                    <SettingRow label="Instructions" isOptional>
                      {({ inputId }) => <span className={builder.fill}><Input id={inputId} disabled={isReadOnly} value={section.instructions} placeholder="Choose the best answer." onChange={(event) => setSection(index, { instructions: event.target.value })} /></span>}
                    </SettingRow>
                    <div className={builder.plainRow}>
                      <Checkbox checked={section.isDrawing} disabled={isReadOnly || section.questions.length < 2} label="Give each candidate only some of these, picked at random" onChange={(isDrawing) => setSection(index, { isDrawing, drawCount: Math.max(1, Math.min(section.drawCount, section.questions.length - 1)) })} />
                      {section.isDrawing && (
                        <span className={builder.drawCount}>
                          <Input aria-label="How many to give" isNumeric inputMode="numeric" disabled={isReadOnly} value={String(section.drawCount)} onChange={(event) => setSection(index, { drawCount: wholeNumber(event.target.value) })} />
                          <span className={builder.headMeta}>of {section.questions.length}</span>
                        </span>
                      )}
                    </div>
                    {problem && section.questions.length > 0 && (
                      <p className={builder.problem} role="alert">
                        {problem}
                      </p>
                    )}

                    {section.questions.length > 0 && (
                      <ol className={builder.pool} aria-label="Questions in the pool">
                        {section.questions.map((question, questionIndex) => (
                          <li key={question.familyId} className={builder.poolItem}>
                            <span className={builder.badge} aria-hidden>
                              {questionIndex + 1}
                            </span>
                            <span className={builder.poolText}>
                              <span className={builder.poolQuestion}>{question.questionText}</span>
                              <span className={styles.meta}>
                                {question.typeLabel} · {plural(question.points, 'mark')}
                                {question.isAutoMarked ? '' : ' · marked by a person'}
                              </span>
                            </span>
                            {question.hasNewerVersion && (
                              <Chip tone="attention" plain>
                                Newer wording — saving uses it
                              </Chip>
                            )}
                            {!isReadOnly && (
                              <span className={builder.poolActions}>
                                <Button size="sm" variant="ghost" icon={ArrowUp} aria-label="Move up" disabled={questionIndex === 0} onClick={() => move(index, questionIndex, -1)} />
                                <Button size="sm" variant="ghost" icon={ArrowDown} aria-label="Move down" disabled={questionIndex === section.questions.length - 1} onClick={() => move(index, questionIndex, 1)} />
                                <Button size="sm" variant="ghost" icon={Trash2} aria-label={`Remove ${question.questionText}`} onClick={() => setSection(index, { questions: section.questions.filter((_, i) => i !== questionIndex) })} />
                              </span>
                            )}
                          </li>
                        ))}
                      </ol>
                    )}
                    {!isReadOnly && (
                      <div className={builder.addRow}>
                        <Button variant="outline" size="sm" icon={Plus} disabled={chosenVenture === ''} onClick={() => setPicking(index)}>
                          Add questions from the bank
                        </Button>
                      </div>
                    )}
                  </div>
                </Card>
              </section>
            );
          })}

          {!isReadOnly && sections.length < ASSESSMENT_SECTIONS_MAX && (
            <div>
              <Button variant="outline" icon={Plus} onClick={() => { setSections([...sections, newSection(sections.length)]); touch(); }}>
                Add a section
              </Button>
            </div>
          )}

          {/* Saving stays explicit: every save is a new version, so autosave would version every keystroke (Redesign Group 6). */}
          {canEdit && (
            <div className={builder.saveBar} role="region" aria-label="Saving">
              <Button variant="ghost" size="sm" onClick={() => void navigate({ to: '/assessments' })}>
                Cancel
              </Button>
              <Button variant="solid" size="sm" disabled={!isValid} isPending={save.isPending} onClick={() => void onSave()}>
                {existing ? `Save as version ${existing.versionNumber + 1}` : 'Save test'}
              </Button>
            </div>
          )}
        </div>

        <aside className={builder.summary} aria-labelledby="test-gets">
          <h2 id="test-gets" className={builder.summaryTitle}>
            What each candidate gets
          </h2>
          <dl className={builder.figures}>
            <div className={builder.figure}>
              <dt>questions</dt>
              <dd>{given}</dd>
            </div>
            <div className={builder.figure}>
              <dt>marks</dt>
              <dd>{totals.totalPoints}</dd>
            </div>
            <div className={builder.figure}>
              <dt>minutes</dt>
              <dd>{totals.durationMinutes}</dd>
            </div>
          </dl>
          <p className={builder.sentence}>
            {plural(given, 'question')} for {totals.totalPoints} marks in {totals.durationMinutes} minutes. Passing a job's pass mark of {passPercent}% needs {Math.ceil((passPercent / 100) * totals.totalPoints)} marks.
          </p>
          {hasWritten ? (
            <p className={builder.warning}>Some answers are marked by a person, so nobody moves on to an interview automatically until they are marked.</p>
          ) : (
            <p className={builder.note}>Every answer is marked by the system the moment the test is submitted.</p>
          )}
        </aside>
      </div>

      <QuestionPicker
        isOpen={picking !== null}
        onClose={() => setPicking(null)}
        sectionName={picking === null ? '' : sections[picking]?.name ?? ''}
        chosenFamilyIds={new Set(picking === null ? [] : sections[picking]?.questions.map((question) => question.familyId) ?? [])}
        ventureId={chosenVenture}
        onAdd={(picked) => picking !== null && addQuestions(picking, picked)}
        onNewQuestion={() => setIsWriting(true)}
      />
      <QuestionEditor
        isOpen={isWriting}
        onClose={() => setIsWriting(false)}
        question={null}
        ventures={ventureList.filter((venture) => venture.id === chosenVenture)}
        defaultVentureId={chosenVenture}
        onSaved={(question) => picking !== null && addQuestions(picking, [question])}
      />
    </div>
  );
}
